// utils
import { addDays, startOfDay } from 'date-fns';

/** a period of whole days, both ends inclusive */
export type DayPeriod = {
  start: Date;
  end: Date;
};

/**
 * A period an already-registered absence occupies, with the work agreements it
 * covers. A duty line on one of those work agreements inside this period
 * belongs to that absence, not to the one being registered.
 */
export type CoveredAbsencePeriod = DayPeriod & {
  waIds: number[];
};

/**
 * Does this day fall inside one of the periods?
 *
 * Both ends count, and the time of day is dropped: the callers hand in whatever
 * they have — a duty's start time, a day from the calendar — and mean the day.
 */
export const isDayCovered = (date: Date, periods: DayPeriod[]): boolean => {
  const day = startOfDay(date);

  return periods.some(
    ({ start, end }) => day >= startOfDay(start) && day <= startOfDay(end)
  );
};

/**
 * The first day from `date` onwards that no period covers — `date` itself when
 * it is already free.
 *
 * Used for the rollout start: the period may overlap another absence (that is
 * what keeps the overlap visible), but the day new sickness duties start being
 * created may not.
 */
export const firstFreeDay = (date: Date, periods: DayPeriod[]): Date => {
  let day = startOfDay(date);
  // the periods can touch, so stepping past one may land inside the next
  while (isDayCovered(day, periods)) {
    day = addDays(day, 1);
  }

  return day;
};

/**
 * End a sentence with exactly one period. A Dutch month abbreviation often
 * carries one already ("28 sep."), "mei" does not; a second one would read as
 * a typo ("28 sep..").
 */
export const endSentence = (text: string): string => text.replace(/\.?$/, '.');
